import { useQuery } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';

import { DataTable } from '@/components/composed/DataTable/DataTable';
import { type TableFeatures } from '@/components/composed/DataTable/table-features';
import type { Permission } from '@/lib/api/domains/permissions';
import { permissionCatalogQuery } from '../api/permission-queries';

/*
 * The permission catalog screen (F03 R41). Built for task 2.2 against the
 * task-2.1 tests in PermissionCatalog.test.tsx:
 *
 *   - AC-51f2752f — THE permission catalog SHALL show each permission as the
 *     kind of record it governs PAIRED WITH the action it permits. The pairing
 *     is a column of record-kinds against a column of actions, answerable from
 *     the one row: a "Kind of record" column and an "Action" column, each
 *     permission one row.
 *   - AC-3ce30755 — WHERE a permission carries a description, THE catalog SHALL
 *     show that description WITH the permission (on its row's Description cell).
 *     A permission saved with no description (AC-9d86cbd6, Identity-accepted) is
 *     still listed as record-kind + action — its Description cell is simply
 *     empty, never a stand-in sentence.
 *
 * The catalog is relayed from Identity unchanged (domain-svc internal/rbac); the
 * screen reads `permissionCatalogQuery` and renders EXACTLY those rows,
 * composing no catalog entry of its own. The record-kind is shown by its
 * human-readable feature label where Identity returns one, falling back to the
 * `resource` key so a reader never sees a bare key.
 *
 * F03 records; F03b refuses — nothing here is an authorization decision. The
 * screen only displays what the catalog contains; it withholds and refuses
 * nothing.
 */
export function PermissionCatalog(): React.ReactElement {
  const { data, isLoading, isError } = useQuery(permissionCatalogQuery());
  const rows = data ?? [];

  const columns: Array<ColumnDef<TableFeatures, Permission>> = [
    {
      // The kind of record the permission governs, paired on the row with the
      // action (AC-51f2752f). Shown by the feature's human-readable label where
      // Identity returns one, else the resource key — never a bare key alone.
      id: 'recordKind',
      header: 'Kind of record',
      accessorKey: 'resource',
      cell: ({ row }) => (
        <span className="font-medium">
          {row.original.resourceLabel ?? row.original.resource}
        </span>
      ),
    },
    {
      // The action the permission permits — the free text the administrator
      // typed, paired with the record-kind on the one row (AC-51f2752f).
      id: 'action',
      header: 'Action',
      accessorKey: 'action',
      cell: ({ row }) => <span>{row.original.action}</span>,
    },
    {
      // The description, shown WITH the permission only WHERE one is present
      // (AC-3ce30755). A permission with none (AC-9d86cbd6) renders an empty
      // cell — it is still listed as record-kind + action above.
      id: 'description',
      header: 'Description',
      accessorKey: 'description',
      cell: ({ row }) =>
        row.original.description ? (
          <span className="text-muted-foreground">
            {row.original.description}
          </span>
        ) : null,
    },
  ];

  return (
    <section aria-label="Permission catalog">
      <DataTable<Permission>
        columns={columns}
        data={rows}
        getRowId={(row) => row.id}
        tableId="permission-catalog"
        isLoading={isLoading}
        isError={isError}
        errorMessage="Failed to load the permission catalog."
        emptyTitle="No permissions"
        emptyDescription="No permissions have been defined yet."
      />
    </section>
  );
}
